import { useEffect, useCallback, useRef } from 'react';
import * as signalR from '@microsoft/signalr';
import { useNotifications } from '@/components/ui/NotificationSystem';
import { useCurrentUser } from '@/hooks/useCurrentUser';
import { navigateApp } from '@/lib/navigation';

export interface NewMessageEvent {
    conversation_id: string;
    company_id: string;
    technician_id: string;
    sender_type: 'Technician' | 'Company';
    message_preview: string;
}

type NewMessageHandler = (event: NewMessageEvent) => void;

/**
 * Hook that listens for NewMessage events from the Realtime service.
 * Shows toast notifications when the user is not on the messaging page.
 *
 * Returns a subscribe function so components (e.g. MessagingPage)
 * can register their own handler for live updates.
 */
export const useMessagingNotifications = (connection: signalR.HubConnection | null) => {
    const { addNotification } = useNotifications();
    const { userRole } = useCurrentUser();
    // Foreground subscribers (e.g. MessagingPage) — their presence suppresses toast
    const subscribersRef = useRef<Set<NewMessageHandler>>(new Set());
    // Background subscribers (e.g. badge counter) — never suppress toast
    const backgroundSubscribersRef = useRef<Set<NewMessageHandler>>(new Set());
    const isTechnician = userRole?.toLowerCase() === 'technician';

    const subscribe = useCallback((handler: NewMessageHandler) => {
        subscribersRef.current.add(handler);
        return () => {
            subscribersRef.current.delete(handler);
        };
    }, []);

    const subscribeBackground = useCallback((handler: NewMessageHandler) => {
        backgroundSubscribersRef.current.add(handler);
        return () => {
            backgroundSubscribersRef.current.delete(handler);
        };
    }, []);

    useEffect(() => {
        if (!connection) return;

        const handler = (event: NewMessageEvent) => {
            // Notify foreground subscribers (e.g. MessagingPage for live chat updates)
            const hasSubscriber = subscribersRef.current.size > 0;
            subscribersRef.current.forEach((fn) => fn(event));
            // Notify background subscribers (e.g. badge counter) — never suppresses toast
            backgroundSubscribersRef.current.forEach((fn) => fn(event));

            // If no foreground component is actively handling messages → show toast notification
            if (!hasSubscriber) {
                const senderLabel = isTechnician
                    ? 'Office'
                    : `Technician`;

                addNotification({
                    type: 'info',
                    title: `New message from ${senderLabel}`,
                    message: event.message_preview,
                    duration: 5000,
                    onClick: () => {
                        navigateApp('/messaging');
                    },
                });
            }
        };

        connection.on('NewMessage', handler);
        return () => {
            connection.off('NewMessage', handler);
        };
    }, [connection, isTechnician, addNotification]);

    return { subscribe, subscribeBackground };
};
